import { HealingOutlined } from '@mui/icons-material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HomeIcon from '@mui/icons-material/Home';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import MessageDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import {
  editAdmission,
  fetchAdmissionParticular,
  resetCurrentAdmission
} from '../../redux/admissions/admissionsActions';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import {
  fetchAllPatients,
  fetchPatient,
} from '../../redux/patient/patientAction';
import { fetchWards } from '../../redux/ward/wardAction';

const AdmissionEdit = () => {
  // State management
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: '',
    message: '',
    type: 'success',
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [floorsLoaded, setFloorsLoaded] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Helper functions
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Form initialization
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: '',
      block_id: '',
      floor_id: '',
      ward_id: '',
      department_id: '',
      admission_status: '',
      attender_type: '1',
      no_of_attenders: '1',
      attender_name: '',
      attender_relation: '',
      attender_address: '',
      attender_phone: '',
      attender_name_2: '',
      attender_relation_2: '',
      attender_address_2: '',
      attender_phone_2: '',
      admit_date: getCurrentDate(),
    },
  });

  // Form field watch values
  const watchPatientId = watch('patient_id');
  const watchAttenderType = watch('attender_type');
  const watchNoOfAttenders = watch('no_of_attenders');
  const watchBlock = watch('block_id');
  const watchFloor = watch('floor_id');
  const watchWard = watch('ward_id');

  // Redux selectors
  const admissionData = useSelector((state) => state?.admission?.fetchAdmissionParticular?.admission || {});
  const patientState = useSelector((state) => state?.patients?.fetchPatient || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const departmentList = useSelector((state) => state?.department?.fetchDepartments || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const patientList = useSelector((state) => state?.patients?.fetchAllListPatients || {});
  const admissionState = useSelector((state) => state?.admission?.addAdmission || {});

console.log(admissionData, 'admissionData');

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch all required base data in parallel
        await Promise.all([
          dispatch(fetchBlocks()),
          dispatch(fetchDepartments()),
          dispatch(fetchAllPatients()),
          dispatch(fetchAdmissionParticular(id)),
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
        setDialogProps({
          title: 'Error',
          message: 'Failed to load admission data. Please try again.',
          type: 'error',
        });
        setDialogOpen(true);
      }
    };

    if (id) {
      fetchInitialData();
    }
  }, [dispatch, id]);

  // Set form values when both admission data and patient list are available
  useEffect(() => {
    const setFormValues = async () => {
      // Check if we already initialized to prevent multiple runs
      if (dataInitialized) {
        return;
      }

      // Wait for both admission data and patient list
      if (!admissionData?.data || !patientList?.data?.data || patientList.data.data.length === 0) {
       
        return;
      }

      try {
        const admissionDetails = admissionData.data;
        
        console.log('DEBUG: admissionDetails:', admissionDetails);
        console.log('DEBUG: keys of admissionDetails:', Object.keys(admissionDetails || {}));

        setIsDataLoaded(false);
        setFloorsLoaded(false);

        // Set Patient Information
        if (admissionDetails?.patient_id) {
          setValue('patient_id', admissionDetails.patient_id);
          await dispatch(fetchPatient(admissionDetails.patient_id));
        }

        // Set Department Information
        if (admissionDetails?.department_id) {
          setValue('department_id', admissionDetails.department_id);
        }

        // Set Admission Details
        setValue('admission_no', admissionDetails?.admission_no || '');
        
        // Make sure admission status is set as a number for the select field
        if (admissionDetails?.admission_status) {
          console.log('Setting admission status:', admissionDetails.admission_status);
          setValue('admission_status', parseInt(admissionDetails.admission_status));
        }

        // Format and set admission date
        if (admissionDetails?.admit_date) {
          const date = new Date(admissionDetails.admit_date);
          setValue('admit_date', date.toISOString().split('T')[0]);
        }

        // Set Attender Information
        setValue('attender_type', admissionDetails?.attender_type?.toString() || '1');
        setValue('no_of_attenders', admissionDetails?.number_attenders?.toString() || '1');

        // Set First Attender Details
        if (admissionDetails?.Attenders && admissionDetails.Attenders.length > 0) {
          const attender = admissionDetails.Attenders[0];
          setValue('attender_name', attender.attender_name || '');
          setValue('attender_relation', attender.relation?.toString() || '');
          setValue('attender_address', attender.address || '');
          setValue('attender_phone', attender.phone || '');
          setValue('attender_serial_no', attender.attender_serial_no || '');

          // Set Second Attender Details if exists
          if (admissionDetails.Attenders.length > 1) {
            const secondAttender = admissionDetails.Attenders[1];
            setValue('attender_name_2', secondAttender.attender_name || '');
            setValue('attender_relation_2', secondAttender.relation?.toString() || '');
            setValue('attender_address_2', secondAttender.address || '');
            setValue('attender_phone_2', secondAttender.phone || '');
            setValue('attender_serial_no_2', secondAttender.attender_serial_no || '');
          }
        }

        // Set related information from nested objects
        if (admissionDetails?.Patient) {
          setValue('patient_name', admissionDetails.Patient.patient_name || '');
          setValue('patient_serial_no', admissionDetails.Patient.patient_serial_no || '');
          setValue('ip_number', admissionDetails.Patient.ip_number || '');
        }

        if (admissionDetails?.Department) {
          setValue('department_name', admissionDetails.Department.department_name || '');
        }

        if (admissionDetails?.Ward) {
          setValue('ward_name', admissionDetails.Ward.ward_name || '');
        }

        if (admissionDetails?.Floor) {
          setValue('floor_name', admissionDetails.Floor.floor_name || '');
        }

        if (admissionDetails?.Block) {
          setValue('block_name', admissionDetails.Block.block_name || '');
        }

        // Set Location Information - Fetch and set in sequence
        const blockId = admissionDetails?.block_id || '';
        if (blockId) {
          setValue('block_id', blockId);
          localStorage.setItem('block', blockId);
          
          // Fetch floors for the block
          await dispatch(fetchFloors(blockId));
          setFloorsLoaded(true);
        }

        // Mark as initialized to prevent re-running
        setDataInitialized(true);
        
      } catch (error) {
        console.error('Error setting form values:', error);
        setDialogProps({
          title: 'Error',
          message: 'Failed to set form values. Please try again.',
          type: 'error',
        });
        setDialogOpen(true);
      }
    };

    setFormValues();
  }, [admissionData, patientList, dispatch, setValue, dataInitialized]);

  // Set floor value after floors are loaded
  useEffect(() => {
    if (floorsLoaded && admissionData?.data?.floor_id && floorList?.floors?.data?.length > 0) {
      const floorId = admissionData.data.floor_id;
      const blockId = admissionData.data.block_id;
      
      console.log('Setting floor:', floorId);
      setValue('floor_id', floorId);
      localStorage.setItem('floor', floorId);
      
      // Fetch wards after floor is set
      if (blockId && floorId) {
        dispatch(fetchWards(blockId, floorId));
      }
    }
  }, [floorsLoaded, floorList, admissionData, dispatch, setValue]);

  // Set ward value after wards are loaded
  useEffect(() => {
    if (floorsLoaded && admissionData?.data?.ward_id && wardList?.wards?.data?.length > 0) {
      const wardId = admissionData.data.ward_id;
      
      // Check if this ward exists in the loaded ward list
      const wardExists = wardList.wards.data.some(ward => ward.id === wardId);
      
      if (wardExists) {
        console.log('Setting ward:', wardId);
        setValue('ward_id', wardId);
        localStorage.setItem('ward', wardId);
        
        // Allow change handlers to work after initial load
        if (!isDataLoaded) {
          setTimeout(() => {
            setIsDataLoaded(true);
          }, 300);
        }
      }
    }
  }, [floorsLoaded, wardList, admissionData, setValue, isDataLoaded]);

  // Handle block selection change (only for user interactions after initial load)
  useEffect(() => {
    if (!isDataLoaded) return;

    const handleBlockChange = async () => {
      if (watchBlock) {
        try {
          const currentBlock = localStorage.getItem('block');
          
          if (watchBlock !== currentBlock) {
            localStorage.setItem('block', watchBlock);
            setValue('floor_id', '');
            setValue('ward_id', '');
            localStorage.removeItem('floor');
            localStorage.removeItem('ward');
            await dispatch(fetchFloors(watchBlock));
          }
        } catch (error) {
          console.error('Error handling block change:', error);
        }
      }
    };

    handleBlockChange();
  }, [watchBlock, isDataLoaded, dispatch, setValue]);

  // Handle floor selection change (only for user interactions after initial load)
  useEffect(() => {
    if (!isDataLoaded) return;

    const handleFloorChange = async () => {
      if (watchFloor && watchBlock) {
        try {
          const currentFloor = localStorage.getItem('floor');
          
          if (watchFloor !== currentFloor) {
            localStorage.setItem('floor', watchFloor);
            setValue('ward_id', '');
            localStorage.removeItem('ward');
            await dispatch(fetchWards(watchBlock, watchFloor));
          }
        } catch (error) {
          console.error('Error handling floor change:', error);
        }
      }
    };

    handleFloorChange();
  }, [watchFloor, watchBlock, isDataLoaded, dispatch, setValue]);

  // Handle ward selection change
  useEffect(() => {
    if (watchWard && isDataLoaded) {
      localStorage.setItem('ward', watchWard);
    }
  }, [watchWard, isDataLoaded]);

  // Handle patient data when it's loaded (for patient details auto-fill)
  useEffect(() => {
    if (patientState?.success && patientState?.data?.data) {
      const patient = patientState.data.data;

      if (!watch('patient_name')) {
        setValue('patient_name', patient.name || '');
      }
      if (!watch('patient_address')) {
        setValue('patient_address', patient.address || '');
      }
      if (!watch('patient_phone')) {
        setValue('patient_phone', patient.User?.phone || '');
      }
    }
  }, [patientState, setValue, watch]);

  // Handle submission success/error
  useEffect(() => {
    if (admissionState.success) {
      setDialogProps({
        title: 'Success',
        message: 'Admission updated successfully!',
        type: 'success',
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    } else if (admissionState.error) {
      setDialogProps({
        title: 'Error',
        message: 'Failed to update admission. Please try again.',
        type: 'error',
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    }
  }, [admissionState, dispatch]);

  // Option generators
  const getDepartmentOptions = () => {
    return departmentList.departments
      ? departmentList.departments
          .filter((department) => department.status !== 2)
          .map((department) => ({
            value: department.id,
            label: department.department_name,
          }))
      : [];
  };

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  const handleCancel = () => {
    navigate(APP_LINK.LISTADMISSIONDETAILS);
  };

  const onSubmit = (data) => {
    console.log('Form data being submitted:', data);
    const formattedDate = data.admit_date
      ? `${data.admit_date} 00:00:00`
      : getCurrentDate() + ' 00:00:00';
    const formdata = {
      patient_id: data.patient_id,
      block_id: data.block_id,
      floor_id: data.floor_id,
      ward_id: data.ward_id,
      department_id: data.department_id || '',
      admit_date: formattedDate,
      attender_type: data.attender_type,
    };
    const attenderDetails = [];
    if (data.attender_type === '1') {
      attenderDetails.push({
        attender_name: data.attender_name,
        relation: data.attender_relation,
        address: data.attender_address,
        phone: data.attender_phone,
      });

      if (data.no_of_attenders === '2') {
        attenderDetails.push({
          attender_name: data.attender_name_2,
          relation: data.attender_relation_2,
          address: data.attender_address_2,
          phone: data.attender_phone_2,
        });
      }
      formdata.number_attenders = data.no_of_attenders;
      formdata.attender_details = attenderDetails;
    } else {
      formdata.number_attenders = '0';
      formdata.attender_details = [];
    }

    if (!formdata.block_id || !formdata.floor_id || !formdata.ward_id) {
      setDialogProps({
        title: 'Error',
        message: 'Please select Block, Floor and Ward',
        type: 'error',
      });
      setDialogOpen(true);
      return;
    }

    console.log('Final formdata being dispatched:', formdata);
    dispatch(editAdmission(id, formdata));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);

    if (dialogProps.type === 'success') {
      navigate(APP_LINK.LISTADMISSIONDETAILS);
    }
  };

  const patientFields = [
    {
      name: 'patient_id',
      label: 'Patient Name / நோயாளியை தேர்ந்தெடுக்கவும்',
      type: 'select',
      options:
        patientList?.data?.data?.map((patient) => ({
          value: patient.id,
          label: `${patient.patient_name} - ${patient.phone}`,
        })) || [],
      rules: { required: 'Patient selection is required' },
      startIcon: <PersonIcon />,
      readOnly: true,
    },
    {
      name: 'admit_date',
      label: 'Admission Date / சேர்க்கை தேதி',
      type: 'date',
      tabIndex: 1,
      rules: { required: 'Admit Date is required' },
      startIcon: <PersonIcon />,
    },
  ];

  const locationFields = [
    {
      name: 'block_id',
      label: 'Block / கட்டிடம்',
      placeholder: 'Select block',
      type: 'select',
      tabIndex: 2,
      options: getBlockOptions(),
      rules: { required: 'Block is required' },
      startIcon: <LocalHospitalIcon />,
    },
    {
      name: 'ward_id',
      label: 'Ward Name / வார்டு பெயர்',
      placeholder: 'Select ward name',
      type: 'select',
      tabIndex: 4,
      options: getWardOptions(),
      rules: { required: 'Ward name is required' },
      startIcon: <MeetingRoomIcon />,
    },
  ];

  const wardDeptFields = [
    {
      name: 'floor_id',
      label: 'Floor / மாடி',
      placeholder: 'Select Floor',
      type: 'select',
      tabIndex: 3,
      options: getFloorOptions(),
      rules: { required: 'Floor is required' },
      startIcon: <ApartmentIcon />,
    },
    {
      name: 'department_id',
      label: 'Department / துறை',
      placeholder: 'Select department',
      type: 'select',
      tabIndex: 5,
      options: getDepartmentOptions(),
      rules: { required: 'Department is required' },
      startIcon: <HealingOutlined />,
    },
    {
      name: 'admission_status',
      label: 'Admission Status / சேர்க்கை நிலை',
      placeholder: 'Select admission status',
      type: 'select',
      tabIndex: 6,
      options: [
        { value: 1, label: 'Admitted / சேர்க்கை செய்யப்பட்ட' },
        { value: 3, label: 'Discharged / வெளியேற்றப்பட்டது' },
        { value: 4, label: 'Death / இறப்பு' },
        { value: 5, label: 'Abscond / காணாமல் போன' },
        { value: 6, label: 'Reference hospital / குறிப்பு மருத்துவமனை' },
        { value: 7, label: 'AMA / தன்னிச்சையாக வெளியேற்றம்' },
      ],
      rules: { required: 'Admission status is required' },
      startIcon: <HealingOutlined />,
      readOnly: true,
    },
  ];

  const attenderTypeField = [
    {
      name: 'attender_type',
      label: 'Attender Type / வருகையாளர் வகை',
      placeholder: 'Select attender type',
      type: 'select',
      tabIndex: 6,
      options: [
        { value: '1', label: 'Known Attender / தெரிந்த அட்டெண்டர்' },
        { value: '2', label: 'No Attender / அட்டெண்டர் இல்லை' },
      ],
      rules: { required: 'Attender type is required' },
      startIcon: <HealingOutlined />,
    },
  ];

  const attenderNumberField = [
    {
      name: 'no_of_attenders',
      label: 'Number of Attenders / வருகையாளர்களின் எண்ணிக்கை ',
      placeholder: 'Enter number of attenders (1 or 2)',
      type: 'text',
      tabIndex: 7,
      rules: {
        required: 'Number of attenders is required',
        pattern: {
          value: /^[1-2]$/,
          message: 'Only 1 or 2 attenders are allowed',
        },
      },
      startIcon: <PersonIcon />,
    },
  ];

  const firstAttenderFields = [
    {
      name: 'attender_name',
      label: 'Attender Name / வருகையாளர் பெயர்',
      placeholder: 'Enter attender name',
      type: 'text',
      tabIndex: 8,
      rules: { required: 'Attender name is required' },
      startIcon: <PersonIcon />,
    },
    {
      name: 'attender_relation',
      label: 'Relation with Patient / நோயாளியுடனான உறவு',
      placeholder: 'Enter relation',
      type: 'select',
      tabIndex: 9,
      options: [
        { value: '1', label: 'Spouse / வாழ்க்கைத் துணை' },
        { value: '2', label: 'Parent / பெற்றோர்' },
        { value: '3', label: 'Child / குழந்தை' },
        { value: '4', label: 'Sibling / உடன்பிறப்பு' },
        { value: '5', label: 'Friend / நண்பர்' },
        { value: '6', label: 'Other / வேறு' },
      ],
      rules: { required: 'Relation is required' },
      startIcon: <FamilyRestroomIcon />,
    },
    {
      name: 'attender_address',
      label: 'Address / முகவரி',
      placeholder: 'Enter address',
      type: 'text',
      tabIndex: 10,
      multipleLine: true,
      rules: { required: 'Address is required' },
      startIcon: <HomeIcon />,
    },
    {
      name: 'attender_phone',
      label: 'Phone Number /  தொலைபேசி எண்',
      placeholder: 'Enter phone number',
      type: 'text',
      tabIndex: 11,
      maxLength: 10,
      showCharCount: false,
      rules: {
        pattern: {
          value: /^[0-9]{10}$/,
          message: 'Please enter a valid 10-digit phone number',
        },
      },
      startIcon: <PhoneIcon />,
    },
  ];

  const secondAttenderFields = [
    {
      name: 'attender_name_2',
      label: 'Second Attender Name / இரண்டாவது வருகையாளர் பெயர்',
      placeholder: 'Enter attender name',
      type: 'text',
      tabIndex: 12,
      rules: { required: 'Second attender name is required' },
      startIcon: <PersonIcon />,
    },
    {
      name: 'attender_relation_2',
      label: 'Relation with Patient / நோயாளியுடனான உறவு',
      placeholder: 'Enter relation',
      type: 'select',
      tabIndex: 13,
      options: [
        { value: '1', label: 'Spouse / வாழ்க்கைத் துணை' },
        { value: '2', label: 'Parent / பெற்றோர்' },
        { value: '3', label: 'Child / குழந்தை' },
        { value: '4', label: 'Sibling / உடன்பிறப்பு' },
        { value: '5', label: 'Friend / நண்பர்' },
        { value: '6', label: 'Other / வேறு' },
      ],
      rules: { required: 'Relation is required' },
      startIcon: <FamilyRestroomIcon />,
    },
    {
      name: 'attender_address_2',
      label: 'Address / முகவரி',
      placeholder: 'Enter address',
      type: 'text',
      tabIndex: 14,
      multipleLine: true,
      rules: { required: 'Address is required' },
      startIcon: <HomeIcon />,
    },
    {
      name: 'attender_phone_2',
      label: 'Phone Number /  தொலைபேசி எண்',
      placeholder: 'Enter phone number',
      type: 'text',
      tabIndex: 15,
      maxLength: 10,
      showCharCount: false,
      rules: {
        pattern: {
          value: /^[0-9]{10}$/,
          message: 'Please enter a valid 10-digit phone number',
        },
      },
      startIcon: <PhoneIcon />,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Typography variant='h4' sx={styles.heading}>
          Edit Admission
        </Typography>
        <Typography variant='subtitle1' sx={{ mb: 4 }}>
          Update admission details
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <ReusableFormFields
                  inputFields={patientFields}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  gridProps={{ xs: 12, lg: 6 }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Current Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={locationFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={wardDeptFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Attender Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={attenderTypeField}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>

                {watchAttenderType === '1' && (
                  <Grid item xs={12} sm={6}>
                    <ReusableFormFields
                      inputFields={attenderNumberField}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      control={control}
                      gridProps={{ xs: 12 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {watchAttenderType === '1' && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {watchNoOfAttenders === '1' || watchNoOfAttenders === '2' ? (
                    <Grid item xs={12} sm={watchNoOfAttenders === '2' ? 6 : 12}>
                      <Typography variant='h6' sx={{ mb: 2 }}>
                        First Attender Details
                      </Typography>
                      <ReusableFormFields
                        inputFields={firstAttenderFields}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        gridProps={{ xs: 12 }}
                      />
                    </Grid>
                  ) : null}
                  {watchNoOfAttenders === '2' && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant='h6' sx={{ mb: 2 }}>
                        Second Attender Details
                      </Typography>
                      <ReusableFormFields
                        inputFields={secondAttenderFields}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        gridProps={{ xs: 12 }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={styles.buttonsContainer}>
                <CustomButton
                  text='Cancel'
                  variant='contained'
                  size='small'
                  onClick={handleCancel}
                  tabIndex={15}
                />
                <CustomButton
                  type='submit'
                  text='Update'
                  variant='contained'
                  size='small'
                  tabIndex={16}
                  loading={admissionState?.inProgress}
                  disabled={admissionState?.inProgress}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <MessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={dialogProps.title}
        message={dialogProps.message}
        type={dialogProps.type}
      />
    </Box>
  );
};

export default AdmissionEdit;